import type { ChangeEvent, FormEvent, MouseEvent, ReactElement } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getAuthHeaders } from "../api/auth";
import type {
  UICompositionActionDefinition,
  UICompositionDataSourceDefinition,
  UICompositionDefinition,
  UICompositionLayoutNode
} from "./types";
import {
  coerceFieldFormat,
  formatResultValue,
  type FieldFormatDescriptor
} from "./formatting";
import { formatTimingStatus, isTimingStatus } from "./timingStatus";

interface UICompositionViewProps {
  composition: UICompositionDefinition;
  resourceIdentifier: string;
  apiBaseUrl: string;
  language: string;
  queryEntries?: Array<[string, string]>;
  onUnauthorized?: (message?: string) => void;
}

type DataSourceState =
  | { status: "idle" | "loading" } & { resource: string }
  | {
    status: "loaded";
    resource: string;
    items: Array<Record<string, unknown>>;
    total: number;
    filters?: Record<string, unknown>;
  }
  | { status: "error"; resource: string; error: string };

type SortDirection = "asc" | "desc";

interface TableSortState {
  column: string;
  direction: SortDirection;
}

interface ColumnConfig {
  key: string;
  title: string;
  width?: number;
  type?: string;
  emptyText: string;
  format: FieldFormatDescriptor | null;
  sortable: boolean;
}

interface ActionContext {
  row?: Record<string, unknown>;
  rowIndex?: number;
}

type FilterType = "text" | "multiText" | "select" | "datetimeRange";

interface FilterDefinition {
  key: string;
  type: FilterType;
  label?: string;
  placeholder?: string;
  queryParam?: string | { from?: string; to?: string };
  multi?: boolean;
  options?: Array<{ value: string; label: string }>;
  fromLabel?: string;
  toLabel?: string;
}

interface FilterLabelSet {
  sectionTitle: string;
  apply: string;
  reset: string;
  multiPlaceholder: string;
  fromLabel: string;
  toLabel: string;
  toggleShow: string;
  toggleHide: string;
}

interface DateRangeValue {
  from?: string;
  to?: string;
}

const PLACEHOLDER_PATTERN = /\{\{\s*([^}\s][^}]*)\s*\}\}/g;

export function UICompositionView(props: UICompositionViewProps) {
  const { composition, resourceIdentifier, apiBaseUrl, language, onUnauthorized } = props;
  const queryEntries = props.queryEntries ?? [];
  const dataSources = Array.isArray(composition.dataSources)
    ? (composition.dataSources as UICompositionDataSourceDefinition[])
    : [];
  const actions = useMemo(() => {
    return Array.isArray(composition.actions)
      ? (composition.actions as UICompositionActionDefinition[])
      : [];
  }, [composition.actions]);

  const dataSourceFilters = useMemo(() => createFilterDefinitionMap(dataSources), [dataSources]);

  const compositionLabels = useMemo(() => {
    const raw = composition.metadata?.labels;
    return (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
  }, [composition.metadata?.labels]);

  const resolveLabel = useCallback(
    (key: string, fallback: string) => {
      const value = compositionLabels[key];
      if (typeof value === "string" && value.trim().length > 0) {
        return value;
      }
      return fallback;
    },
    [compositionLabels]
  );

  const [dataState, setDataState] = useState<Record<string, DataSourceState>>(() => {
    const initialEntries = dataSources.map((source) => [
      source.key,
      { status: "idle", resource: source.key } as DataSourceState
    ] as const);
    return Object.fromEntries(initialEntries);
  });

  const [sortState, setSortState] = useState<Record<string, TableSortState>>({});
  const sortStateRef = useRef(sortState);

  useEffect(() => {
    sortStateRef.current = sortState;
  }, [sortState]);

  const [filterState, setFilterState] = useState<Record<string, Record<string, unknown>>>({});
  const filterStateRef = useRef(filterState);

  useEffect(() => {
    filterStateRef.current = filterState;
  }, [filterState]);

  const identifierForFetch = composition.id || resourceIdentifier;

  const loadDataSource = useCallback(
    async (source: UICompositionDataSourceDefinition, overrideSort?: TableSortState | null) => {
      const key = source.key;
      setDataState((prev) => ({
        ...prev,
        [key]: { status: "loading", resource: key }
      }));

      try {
        const params = new URLSearchParams(queryEntries);
        if (!params.has("language")) {
          params.set("language", language);
        }

        const sortConfig = overrideSort === undefined ? sortStateRef.current[key] : overrideSort;
        if (sortConfig) {
          params.set("sort", `${sortConfig.column}:${sortConfig.direction}`);
        }
        if (sortConfig === null) {
          params.delete("sort");
        }

        const filterDefinitionsForSource = dataSourceFilters.get(key) ?? [];
        removeFilterQueryParams(params, filterDefinitionsForSource);
        const activeFilters = filterStateRef.current[key];
        if (activeFilters) {
          appendFilterQueryParams(params, activeFilters, filterDefinitionsForSource);
        }

        const queryString = params.toString();
        const endpoint = `${apiBaseUrl}/api/ui-compositions/${encodeURIComponent(identifierForFetch)}/data/${encodeURIComponent(key)}`;
        const url = queryString.length > 0 ? `${endpoint}?${queryString}` : endpoint;

        const response = await fetch(
          url,
          {
            credentials: "include",
            headers: {
              ...getAuthHeaders(),
              "Accept-Language": language
            }
          }
        );

        if (response.status === 401) {
          let message = "Authentifizierung erforderlich. Bitte melde dich erneut an.";
          const contentType = response.headers.get("content-type") ?? "";

          try {
            if (contentType.includes("application/json")) {
              const payload = (await response.json()) as Record<string, unknown> | null;
              const rawMessage = payload && typeof payload.message === "string" ? payload.message.trim() : "";
              if (rawMessage) {
                message = rawMessage;
              }
            } else {
              const text = await response.text();
              if (typeof text === "string" && text.trim().length > 0) {
                message = text.trim();
              }
            }
          } catch {
            // ignore parsing errors and keep fallback message
          }

          onUnauthorized?.(message);
          setDataState((prev) => ({
            ...prev,
            [key]: {
              status: "error",
              resource: key,
              error: message
            }
          }));
          return;
        }

        if (!response.ok) {
          throw new Error(`Data source ${key} request failed (${response.status})`);
        }

        const payload = (await response.json()) as {
          dataSource: string;
          resource: string;
          total: number;
          items: Array<Record<string, unknown>>;
          filters?: Record<string, unknown> | null;
        };

        const sanitizedFilters = sanitizeFilterValues(payload.filters, filterDefinitionsForSource);

        setDataState((prev) => ({
          ...prev,
          [key]: {
            status: "loaded",
            resource: payload.resource,
            total: payload.total,
            items: payload.items ?? [],
            filters: sanitizedFilters
          }
        }));

        setFilterState((prev) => {
          if (filterDefinitionsForSource.length === 0) {
            return prev;
          }

          const next = { ...prev };
          if (sanitizedFilters && Object.keys(sanitizedFilters).length > 0) {
            next[key] = sanitizedFilters;
          } else {
            delete next[key];
          }
          filterStateRef.current = next;
          return next;
        });
      } catch (error) {
        console.error(error);
        setDataState((prev) => ({
          ...prev,
          [key]: {
            status: "error",
            resource: key,
            error: error instanceof Error ? error.message : "Unbekannter Fehler"
          }
        }));
      }
    },
    [apiBaseUrl, dataSourceFilters, identifierForFetch, language, onUnauthorized, queryEntries]
  );

  useEffect(() => {
    dataSources.forEach((source) => {
      void loadDataSource(source);
    });
  }, [dataSources, loadDataSource]);

  const handleSortChange = useCallback((dataSourceKey: string, nextSort: TableSortState | null) => {
    setSortState((prev) => {
      const nextState = { ...prev };
      if (!nextSort) {
        delete nextState[dataSourceKey];
      } else {
        nextState[dataSourceKey] = nextSort;
      }
      return nextState;
    });

    const targetSource = dataSources.find((source) => source.key === dataSourceKey);
    if (targetSource) {
      void loadDataSource(targetSource, nextSort);
    }
  }, [dataSources, loadDataSource]);

  const handleFilterApply = useCallback(
    (dataSourceKey: string, nextFilters: Record<string, unknown>) => {
      const definitions = dataSourceFilters.get(dataSourceKey) ?? [];
      const sanitized = sanitizeFilterValues(nextFilters, definitions);

      setFilterState((prev) => {
        const next = { ...prev };
        if (sanitized && Object.keys(sanitized).length > 0) {
          next[dataSourceKey] = sanitized;
        } else {
          delete next[dataSourceKey];
        }
        filterStateRef.current = next;
        return next;
      });

      const targetSource = dataSources.find((source) => source.key === dataSourceKey);
      if (targetSource) {
        void loadDataSource(targetSource);
      }
    },
    [dataSourceFilters, dataSources, loadDataSource]
  );

  const handleFilterReset = useCallback(
    (dataSourceKey: string) => {
      setFilterState((prev) => {
        if (!(dataSourceKey in prev)) {
          return prev;
        }
        const next = { ...prev };
        delete next[dataSourceKey];
        filterStateRef.current = next;
        return next;
      });

      const targetSource = dataSources.find((source) => source.key === dataSourceKey);
      if (targetSource) {
        void loadDataSource(targetSource);
      }
    },
    [dataSources, loadDataSource]
  );

  const handleAction = useCallback(
    (actionKey: string, context?: ActionContext) => {
      const action = actions.find((candidate) => candidate.key === actionKey);
      if (!action) {
        console.warn(`Unknown action ${actionKey}`);
        return;
      }

      switch (action.kind) {
        case "refresh": {
          const targets = Array.isArray(action.options?.targets)
            ? (action.options?.targets as string[])
            : [];

          const effectiveTargets = targets.length > 0 ? targets : [context?.rowIndex != null ? actionKey : ""];
          effectiveTargets
            .filter((targetKey) => targetKey && dataSources.some((source) => source.key === targetKey))
            .forEach((targetKey) => {
              const source = dataSources.find((entry) => entry.key === targetKey);
              if (source) {
                void loadDataSource(source);
              }
            });
          break;
        }
        case "navigate": {
          const pathTemplate = String(action.options?.path ?? "");
          if (!pathTemplate) {
            return;
          }
          const contextObject = {
            row: context?.row,
            rowIndex: context?.rowIndex
          } satisfies Record<string, unknown>;
          const resolved = applyTemplate(pathTemplate, contextObject);
          if (resolved.startsWith("http")) {
            window.location.href = resolved;
          } else {
            window.location.href = resolved.startsWith("/") ? resolved : `/${resolved}`;
          }
          break;
        }
        case "back": {
          const fallback = typeof action.options?.fallback === "string" ? action.options.fallback : null;
          if (window.history.length > 1) {
            window.history.back();
          } else if (fallback) {
            window.location.href = fallback;
          } else if (document.referrer) {
            window.location.href = document.referrer;
          } else {
            window.location.href = "/";
          }
          break;
        }
        case "workflow": {
          const workflowId = typeof action.options?.workflowId === "string" && action.options.workflowId.trim().length > 0
            ? action.options.workflowId.trim()
            : typeof action.options?.workflowType === "string"
              ? action.options.workflowType.trim()
              : null;

          if (!workflowId) {
            console.warn("Workflow action missing workflowId/workflowType", action);
            return;
          }

          const targetUrl = `/ui/${encodeURIComponent(workflowId)}`;
          window.location.href = targetUrl;
          break;
        }
        default:
          console.warn(`Unhandled action kind ${action.kind}`);
      }
    },
    [actions, dataSources, loadDataSource]
  );

  const layout = composition.layout;

  return (
    <div className="standalone-container">
      {layout ? renderNode(layout, {
        composition,
        dataState,
        language,
        onAction: handleAction,
        sortState,
        onSortChange: handleSortChange,
        filterDefinitions: dataSourceFilters,
        filterState,
        onApplyFilters: handleFilterApply,
        onResetFilters: handleFilterReset,
        resolveLabel
      }) : (
        <div className="standalone-empty">Keine Layout-Konfiguration definiert.</div>
      )}
    </div>
  );
}

interface RenderContext {
  composition: UICompositionDefinition;
  dataState: Record<string, DataSourceState>;
  language: string;
  onAction: (actionKey: string, context?: ActionContext) => void;
  sortState: Record<string, TableSortState>;
  onSortChange: (dataSourceKey: string, sort: TableSortState | null) => void;
  filterDefinitions: Map<string, FilterDefinition[]>;
  filterState: Record<string, Record<string, unknown>>;
  onApplyFilters: (dataSourceKey: string, filters: Record<string, unknown>) => void;
  onResetFilters: (dataSourceKey: string) => void;
  resolveLabel: (key: string, fallback: string) => string;
}

function renderNode(node: UICompositionLayoutNode, context: RenderContext): ReactElement | null {
  const {
    composition,
    dataState,
    language,
    onAction,
    filterDefinitions,
    filterState,
    onApplyFilters,
    onResetFilters,
    resolveLabel
  } = context;
  const { component, props = {}, children = [] } = node;
  const childNodes = Array.isArray(children) ? (children as UICompositionLayoutNode[]) : [];

  switch (component) {
    case "Page": {
      const hasTitle = typeof props.title === "string" && props.title.trim().length > 0;
      const hasDescription = typeof props.description === "string" && props.description.trim().length > 0;

      let headerToolbar: UICompositionLayoutNode | null = null;
      let contentChildren: UICompositionLayoutNode[] = childNodes;

      if (childNodes.length > 0 && childNodes[0]?.component === "Toolbar") {
        [headerToolbar] = childNodes;
        contentChildren = childNodes.slice(1);
      }

      return (
        <div className="ui-page">
          {(hasTitle || headerToolbar) ? (
            <header className="ui-page-header">
              {hasTitle ? <h1>{String(props.title)}</h1> : null}
              {headerToolbar ? (
                <div className="ui-page-header-actions">
                  {renderNode(headerToolbar, context)}
                </div>
              ) : null}
            </header>
          ) : null}
          {hasDescription ? <p className="ui-page-description">{String(props.description)}</p> : null}
          <div className="ui-page-content">
            {contentChildren.map((child, index) => (
              <div
                key={index}
                className={["ui-section", child.component === "Table" ? "ui-section--fill" : null]
                  .filter(Boolean)
                  .join(" ")}
              >
                {renderNode(child, context)}
              </div>
            ))}
          </div>
        </div>
      );
    }
    case "Toolbar": {
      return (
        <div className={`ui-toolbar ui-toolbar-${props.align ?? "start"}`}>
          {children.map((child, index) => (
            <div key={index} className="ui-toolbar-item">
              {renderNode(child, context)}
            </div>
          ))}
        </div>
      );
    }
    case "Button": {
      const actionKey = typeof props.action === "string" ? props.action : null;
      const label = typeof props.label === "string" ? props.label : "Button";
      const variant = typeof props.variant === "string" ? props.variant : "primary";
      return (
        <button
          type="button"
          className={`ui-button ui-button-${variant}`}
          onClick={() => actionKey && onAction(actionKey)}
          disabled={!actionKey}
        >
          {label}
        </button>
      );
    }
    case "DetailList": {
      const dataSourceKey = typeof props.dataSource === "string" ? props.dataSource : "";
      const dataStateEntry = dataState[dataSourceKey];
      const fields = Array.isArray(props.fields) ? (props.fields as Array<Record<string, unknown>>) : [];
      const emptyText = typeof props.emptyText === "string" ? props.emptyText : "Keine Details gefunden";
      const layout = typeof props.layout === "string" ? props.layout : "grid";

      if (!dataStateEntry || dataStateEntry.status === "idle" || dataStateEntry.status === "loading") {
        return <div className="ui-detail-status">Lade Details …</div>;
      }

      if (dataStateEntry.status === "error") {
        return <div className="ui-detail-status ui-detail-error">{dataStateEntry.error}</div>;
      }

      const record = dataStateEntry.status === "loaded" ? dataStateEntry.items[0] : undefined;

      if (!record) {
        return <div className="ui-detail-status ui-detail-empty">{emptyText}</div>;
      }

      return (
        <div className={`ui-detail ui-detail-${layout}`}>
          <dl className="ui-detail-grid">
            {fields.map((field) => {
              const key = String(field.key ?? field.path ?? "field");
              const label = typeof field.label === "string" ? field.label : key;
              const path = typeof field.path === "string" ? field.path : key;
              const type = typeof field.type === "string" ? field.type : undefined;
              const formatDescriptor =
                coerceFieldFormat((field as { format?: unknown }).format) ??
                coerceFieldFormat(type);
              const emptyValue = typeof field.emptyText === "string" ? field.emptyText : "-";
              const sourceRecord = record as Record<string, unknown>;
              const rawValue = path ? resolveExpression(path, sourceRecord) : undefined;
              const displayValue = rawValue == null || rawValue === ""
                ? emptyValue
                : formatResultValue(rawValue, language, formatDescriptor ?? null);

              return (
                <div key={key} className="ui-detail-item">
                  <dt className="ui-detail-label">{label}</dt>
                  <dd className="ui-detail-value">{displayValue}</dd>
                </div>
              );
            })}
          </dl>
        </div>
      );
    }
    case "Table": {
      const dataSourceKey = typeof props.dataSource === "string" ? props.dataSource : "";
      const dataStateEntry = dataState[dataSourceKey];
      const columns = Array.isArray(props.columns) ? (props.columns as Array<Record<string, unknown>>) : [];
      const emptyState = props.emptyState as Record<string, unknown> | undefined;
      const interactions = Array.isArray(props.interactions)
        ? (props.interactions as Array<Record<string, unknown>>)
        : [];
      const enableSorting = props.enableSorting !== false;
      const activeSort = enableSorting ? context.sortState[dataSourceKey] ?? null : null;
      const tableFilters = filterDefinitions.get(dataSourceKey) ?? [];
      const appliedFilters = dataStateEntry && dataStateEntry.status === "loaded"
        ? (dataStateEntry.filters as Record<string, unknown> | undefined) ?? filterState[dataSourceKey]
        : filterState[dataSourceKey];
      const filterLabels: FilterLabelSet = {
        sectionTitle: resolveLabel("label.filters.sectionTitle", "Filter"),
        apply: resolveLabel("label.filters.apply", "Apply"),
        reset: resolveLabel("label.filters.reset", "Reset"),
        multiPlaceholder: resolveLabel("label.filters.multiPlaceholder", "Enter values"),
        fromLabel: resolveLabel("label.filters.from", "From"),
        toLabel: resolveLabel("label.filters.to", "To"),
        toggleShow: resolveLabel("label.filters.toggleShow", "Show filters"),
        toggleHide: resolveLabel("label.filters.toggleHide", "Hide filters")
      };

      return (
        <TableView
          dataSourceKey={dataSourceKey}
          dataStateEntry={dataStateEntry}
          columns={columns}
          emptyState={emptyState}
          language={language}
          onAction={onAction}
          actions={composition.actions ?? []}
          interactions={interactions}
          enableSorting={enableSorting}
          sortConfig={activeSort}
          onSortChange={enableSorting ? (sort) => context.onSortChange(dataSourceKey, sort) : undefined}
          filters={tableFilters}
          appliedFilters={appliedFilters}
          onApplyFilters={tableFilters.length > 0 ? (filters: Record<string, unknown>) => onApplyFilters(dataSourceKey, filters) : undefined}
          onResetFilters={tableFilters.length > 0 ? () => onResetFilters(dataSourceKey) : undefined}
          filterLabels={filterLabels}
          resolveLabel={resolveLabel}
        />
      );
    }
    default:
      return <div className="ui-unsupported">Komponente {component} wird nicht unterstützt.</div>;
  }
}

interface TableViewProps {
  dataSourceKey: string;
  dataStateEntry?: DataSourceState;
  columns: Array<Record<string, unknown>>;
  emptyState?: Record<string, unknown>;
  language: string;
  onAction: (actionKey: string, context?: ActionContext) => void;
  actions: UICompositionActionDefinition[];
  interactions: Array<Record<string, unknown>>;
  enableSorting: boolean;
  sortConfig: TableSortState | null;
  onSortChange?: (sort: TableSortState | null) => void;
  filters: FilterDefinition[];
  appliedFilters?: Record<string, unknown>;
  onApplyFilters?: (filters: Record<string, unknown>) => void;
  onResetFilters?: () => void;
  filterLabels: FilterLabelSet;
  resolveLabel: (key: string, fallback: string) => string;
}

function TableView(props: TableViewProps): ReactElement {
  const {
    dataSourceKey,
    dataStateEntry,
    columns,
    emptyState,
    language,
    onAction,
    actions,
    interactions,
    enableSorting,
    sortConfig,
    onSortChange,
    filters,
    appliedFilters,
    onApplyFilters,
    onResetFilters,
    filterLabels,
    resolveLabel
  } = props;

  const [draftFilters, setDraftFilters] = useState<Record<string, unknown>>(() =>
    cloneFilterValues(appliedFilters, filters)
  );
  const [filtersCollapsed, setFiltersCollapsed] = useState(false);

  useEffect(() => {
    setDraftFilters(cloneFilterValues(appliedFilters, filters));
  }, [appliedFilters, filters]);

  const activeFilterCount = useMemo(
    () => (appliedFilters ? Object.keys(appliedFilters).length : 0),
    [appliedFilters]
  );

  const hasFilterControls = filters.length > 0 && Boolean(onApplyFilters);

  const resolveFilterText = (value: unknown, fallback: string) => {
    if (typeof value !== "string") {
      return fallback;
    }
    const trimmed = value.trim();
    if (!trimmed) {
      return fallback;
    }
    return trimmed.replace(PLACEHOLDER_PATTERN, (_, expression: string) => {
      const key = expression.trim();
      return key ? resolveLabel(key, fallback) : fallback;
    });
  };

  const sectionTitle = resolveFilterText(filterLabels.sectionTitle, "Filter");
  const applyLabel = resolveFilterText(filterLabels.apply, "Apply");
  const resetLabel = resolveFilterText(filterLabels.reset, "Reset");
  const multiPlaceholder = resolveFilterText(filterLabels.multiPlaceholder, "Enter values");
  const fromLabelDefault = resolveFilterText(filterLabels.fromLabel, "From");
  const toLabelDefault = resolveFilterText(filterLabels.toLabel, "To");
  const toggleShowLabel = resolveFilterText(filterLabels.toggleShow, "Show filters");
  const toggleHideLabel = resolveFilterText(filterLabels.toggleHide, "Hide filters");

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!onApplyFilters) {
      return;
    }
    const prepared = sanitizeFilterValues(draftFilters, filters) ?? {};
    setDraftFilters(cloneFilterValues(prepared, filters));
    onApplyFilters(prepared);
  };

  const handleReset = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    setDraftFilters({});
    onResetFilters?.();
  };

  const handleToggleFilters = () => {
    setFiltersCollapsed((prev) => !prev);
  };

  const handleRangeChange = (filterKey: string, part: "from" | "to") => (event: ChangeEvent<HTMLInputElement>) => {
    const rawValue = event.target.value;
    setDraftFilters((prev) => {
      const next = { ...prev };
      const previousRange = isDateRangeValue(prev[filterKey])
        ? { ...(prev[filterKey] as DateRangeValue) }
        : {};
      const isoValue = rawValue ? convertLocalDateTimeToIso(rawValue) : undefined;
      if (isoValue) {
        previousRange[part] = isoValue;
      } else {
        delete previousRange[part];
      }
      if (!previousRange.from && !previousRange.to) {
        delete next[filterKey];
      } else {
        next[filterKey] = previousRange;
      }
      return next;
    });
  };

  const updateDraftValue = (filterKey: string, nextValue: unknown) => {
    setDraftFilters((prev) => {
      const next = { ...prev };
      if (Array.isArray(nextValue)) {
        if (nextValue.length === 0) {
          delete next[filterKey];
        } else {
          next[filterKey] = [...nextValue];
        }
        return next;
      }

      if (isDateRangeValue(nextValue)) {
        const { from, to } = nextValue;
        if (!from && !to) {
          delete next[filterKey];
        } else {
          next[filterKey] = { ...(nextValue as DateRangeValue) };
        }
        return next;
      }

      if (nextValue === undefined || nextValue === null || nextValue === "") {
        delete next[filterKey];
      } else {
        next[filterKey] = nextValue;
      }
      return next;
    });
  };

  const renderFilterControl = (definition: FilterDefinition, controlId: string) => {
    const currentValue = draftFilters[definition.key];

    switch (definition.type) {
      case "text": {
        const value = typeof currentValue === "string" ? currentValue : "";
        const placeholder = resolveFilterText(definition.placeholder, "");
        return (
          <input
            id={controlId}
            className="ui-table-filter-control"
            type="text"
            value={value}
            placeholder={placeholder || undefined}
            onChange={(event) => updateDraftValue(definition.key, event.target.value)}
          />
        );
      }
      case "multiText": {
        const value = Array.isArray(currentValue) ? (currentValue as string[]) : [];
        const placeholder = resolveFilterText(definition.placeholder, multiPlaceholder);
        return (
          <textarea
            id={controlId}
            className="ui-table-filter-control ui-table-filter-textarea"
            rows={3}
            value={value.join("\n")}
            placeholder={placeholder || undefined}
            onChange={(event) => updateDraftValue(definition.key, parseMultiTextInput(event.target.value))}
          />
        );
      }
      case "select": {
        const options = Array.isArray(definition.options) ? definition.options : [];
        if (definition.multi) {
          const selectedValues = Array.isArray(currentValue) ? (currentValue as string[]) : [];
          return (
            <select
              id={controlId}
              className="ui-table-filter-control"
              multiple
              value={selectedValues}
              onChange={(event) => {
                const selected = Array.from(event.target.selectedOptions, (option) => option.value);
                updateDraftValue(definition.key, selected);
              }}
            >
              {options.map((option) => (
                <option key={option.value} value={option.value}>
                  {resolveFilterText(option.label, option.value)}
                </option>
              ))}
            </select>
          );
        }

        const selectedValue = typeof currentValue === "string" ? currentValue : "";
        const placeholder = resolveFilterText(definition.placeholder, "");
        return (
          <select
            id={controlId}
            className="ui-table-filter-control"
            value={selectedValue}
            onChange={(event) => updateDraftValue(definition.key, event.target.value)}
          >
            <option value="">{placeholder || "–"}</option>
            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {resolveFilterText(option.label, option.value)}
              </option>
            ))}
          </select>
        );
      }
      case "datetimeRange": {
        const rangeValue = isDateRangeValue(currentValue) ? (currentValue as DateRangeValue) : {};
        const fromValue = formatDateTimeLocal(rangeValue.from);
        const toValue = formatDateTimeLocal(rangeValue.to);
        const fromLabel = resolveFilterText(definition.fromLabel ?? "", fromLabelDefault);
        const toLabel = resolveFilterText(definition.toLabel ?? "", toLabelDefault);

        return (
          <div className="ui-table-filter-range">
            <label className="ui-table-filter-range-label" htmlFor={controlId}>
              {fromLabel}
            </label>
            <input
              id={controlId}
              className="ui-table-filter-control"
              type="datetime-local"
              value={fromValue}
              onChange={handleRangeChange(definition.key, "from")}
            />
            <label className="ui-table-filter-range-label" htmlFor={`${controlId}-to`}>
              {toLabel}
            </label>
            <input
              id={`${controlId}-to`}
              className="ui-table-filter-control"
              type="datetime-local"
              value={toValue}
              onChange={handleRangeChange(definition.key, "to")}
            />
          </div>
        );
      }
      default:
        return null;
    }
  };

  const filtersContentId = `${dataSourceKey}-filters-content`;

  return (
    <div className="ui-table">
      {hasFilterControls ? (
        <form
          className={`ui-table-filters${filtersCollapsed ? " ui-table-filters--collapsed" : ""}`}
          onSubmit={handleSubmit}
        >
          <fieldset className="ui-table-filters-fieldset">
            <legend className="ui-table-filters-legend">
              <span className="ui-table-filters-title">{sectionTitle}</span>
              <button
                type="button"
                className="ui-button ui-button-secondary ui-table-filters-toggle"
                onClick={handleToggleFilters}
                aria-expanded={!filtersCollapsed}
                aria-controls={filtersContentId}
              >
                {filtersCollapsed ? toggleShowLabel : toggleHideLabel}
                {activeFilterCount > 0 ? (
                  <span className="ui-table-filters-count" aria-hidden="true">
                    {activeFilterCount}
                  </span>
                ) : null}
              </button>
            </legend>
            <div
              className="ui-table-filters-content"
              id={filtersContentId}
              aria-hidden={filtersCollapsed}
            >
              <div className="ui-table-filters-grid">
                {filters.map((definition) => {
                  const controlId = `${dataSourceKey}-filter-${definition.key}`;
                  const labelText = resolveFilterText(definition.label, definition.key);
                  const helperText = definition.type === "select" && definition.multi
                    ? resolveFilterText(definition.placeholder, multiPlaceholder)
                    : null;

                  return (
                    <div key={controlId} className="ui-table-filter">
                      <label className="ui-table-filter-label" htmlFor={controlId}>
                        {labelText}
                      </label>
                      {renderFilterControl(definition, controlId)}
                      {helperText ? <span className="ui-table-filter-hint">{helperText}</span> : null}
                    </div>
                  );
                })}
              </div>
              <div className="ui-table-filters-actions">
                <button type="submit" className="ui-button ui-button-primary">
                  {applyLabel}
                </button>
                <button type="button" className="ui-button ui-button-secondary" onClick={handleReset}>
                  {resetLabel}
                </button>
              </div>
            </div>
          </fieldset>
        </form>
      ) : null}
      {renderTableContent({
        dataSourceKey,
        dataStateEntry,
        columns,
        emptyState,
        language,
        onAction,
        actions,
        interactions,
        enableSorting,
        sortConfig,
        onSortChange
      })}
    </div>
  );
}

interface RenderTableOptions {
  dataSourceKey: string;
  dataStateEntry?: DataSourceState;
  columns: Array<Record<string, unknown>>;
  emptyState?: Record<string, unknown>;
  language: string;
  onAction: (actionKey: string, context?: ActionContext) => void;
  actions: UICompositionActionDefinition[];
  interactions: Array<Record<string, unknown>>;
  enableSorting: boolean;
  sortConfig: TableSortState | null;
  onSortChange?: (sort: TableSortState | null) => void;
}

function renderTableContent(options: RenderTableOptions): ReactElement {
  const { dataStateEntry, columns, emptyState, language, onAction, actions } = options;

  if (!dataStateEntry || dataStateEntry.status === "idle" || dataStateEntry.status === "loading") {
    return <div className="ui-table-loading">Lade Daten …</div>;
  }

  if (dataStateEntry.status === "error") {
    return <div className="ui-table-error">{dataStateEntry.error}</div>;
  }

  const rows = dataStateEntry.status === "loaded" ? dataStateEntry.items : [];
  if (rows.length === 0) {
    const title = typeof emptyState?.title === "string" ? emptyState.title : "Keine Daten";
    const description = typeof emptyState?.description === "string" ? emptyState.description : null;
    return (
      <div className="ui-table-empty">
        <h3>{title}</h3>
        {description ? <p>{description}</p> : null}
      </div>
    );
  }

  const columnConfigs: ColumnConfig[] = columns.map((column) => ({
    key: String(column.key ?? ""),
    title: String(column.title ?? column.key ?? ""),
    width: typeof column.width === "number" ? column.width : undefined,
    type: typeof column.type === "string" ? column.type : undefined,
    emptyText: typeof column.emptyText === "string" ? column.emptyText : "-",
    format: coerceFieldFormat((column as { format?: unknown }).format) ??
      coerceFieldFormat(column.type as unknown) ?? null,
    sortable: (column as { sortable?: unknown }).sortable !== false
  }));

  const findActionByKey = (key: string | undefined | null) =>
    key ? actions.find((action) => action.key === key) ?? null : null;

  const rowClickActionKey = options.interactions
    .filter((interaction) => interaction && interaction.event === "rowClick")
    .map((interaction) => interaction.action)
    .find((action) => typeof action === "string") as string | undefined;

  const rowClickAction = findActionByKey(rowClickActionKey ?? null);

  const handleRowClick = (row: Record<string, unknown>, rowIndex: number) => {
    if (rowClickAction) {
      onAction(rowClickAction.key, { row, rowIndex });
    }
  };

  const handleHeaderClick = (columnKey: string, sortable: boolean) => {
    if (!sortable || !options.onSortChange) {
      return;
    }

    const current = options.sortConfig;
    if (!current || current.column !== columnKey) {
      options.onSortChange({ column: columnKey, direction: "asc" });
      return;
    }

    if (current.direction === "asc") {
      options.onSortChange({ column: columnKey, direction: "desc" });
      return;
    }

    options.onSortChange(null);
  };

  return (
    <div className="ui-table-container">
      <div className="ui-table-shell">
        <div className="ui-table-body">
          <table className="ui-data-table">
            <colgroup>
              {columnConfigs.map((column) => (
                <col key={`col-${column.key}`} style={column.width ? { width: `${column.width}px` } : undefined} />
              ))}
              {rowClickAction ? <col key="col-action" style={{ width: "3rem" }} /> : null}
            </colgroup>
            <thead>
              <tr>
                {columnConfigs.map((column) => (
                  <th key={column.key}>
                    {options.enableSorting && column.sortable ? (
                      <button
                        type="button"
                        className={`ui-table-sort-button${options.sortConfig?.column === column.key ? ` ui-table-sort-button--${options.sortConfig.direction}` : ""}`}
                        onClick={() => handleHeaderClick(column.key, column.sortable)}
                        title="Spalte sortieren"
                      >
                        <span className="ui-table-sort-label">{column.title}</span>
                        <span className="ui-table-sort-icon" aria-hidden="true">
                          {options.sortConfig?.column === column.key
                            ? options.sortConfig.direction === "asc" ? "▲" : "▼"
                            : "↕"}
                        </span>
                      </button>
                    ) : (
                      column.title
                    )}
                  </th>
                ))}
                {rowClickAction ? <th className="ui-table-action-header" aria-hidden="true" /> : null}
              </tr>
            </thead>
            <tbody>
              {rows.map((row: Record<string, unknown>, index: number) => (
                <tr
                  key={`row-${index}`}
                  onClick={() => handleRowClick(row, index)}
                  className={rowClickAction ? "ui-table-clickable" : undefined}
                >
                  {columnConfigs.map((column) => {
                    const rawValue = row[column.key];
                    if (column.type === "statusIndicator") {
                      const status = formatTimingStatus(isTimingStatus(rawValue) ? rawValue : null);
                      const title = status.description ?? status.label;

                      return (
                        <td key={column.key} style={column.width ? { width: `${column.width}px` } : undefined}>
                          <span
                            className={`ui-status-indicator ui-status-${status.color}`}
                            title={title}
                            aria-label={title}
                          >
                            <span className="ui-status-dot" aria-hidden="true" />
                            <span className="ui-status-label">{status.label}</span>
                          </span>
                        </td>
                      );
                    }

                    const displayValue = rawValue == null || rawValue === ""
                      ? column.emptyText
                      : formatResultValue(rawValue, language, (column.format as FieldFormatDescriptor | null) ?? null);

                    return (
                      <td key={column.key} style={column.width ? { width: `${column.width}px` } : undefined}>
                        {displayValue}
                      </td>
                    );
                  })}
                  {rowClickAction ? (
                    <td className="ui-table-action" aria-hidden="true">
                      →
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function applyTemplate(template: string, context: Record<string, unknown>): string {
  return template.replace(PLACEHOLDER_PATTERN, (_, expression: string) => {
    const trimmed = expression.trim();
    if (!trimmed) {
      return "";
    }
    const value = resolveExpression(trimmed, context);
    return value != null ? String(value) : "";
  });
}

function resolveExpression(expression: string, context: Record<string, unknown>): unknown {
  const segments = expression.split(".").map((segment) => segment.trim()).filter(Boolean);
  if (segments.length === 0) {
    return undefined;
  }

  let current: unknown = context;
  for (const segment of segments) {
    if (!current || typeof current !== "object") {
      return undefined;
    }

    current = (current as Record<string, unknown>)[segment];
  }

  return current;
}

function createFilterDefinitionMap(dataSources: UICompositionDataSourceDefinition[]): Map<string, FilterDefinition[]> {
  const map = new Map<string, FilterDefinition[]>();
  dataSources.forEach((source) => {
    const definitions = extractFilterDefinitionsFromSource(source);
    if (definitions.length > 0) {
      map.set(source.key, definitions);
    }
  });
  return map;
}

function extractFilterDefinitionsFromSource(source: UICompositionDataSourceDefinition): FilterDefinition[] {
  const options = source.options as { filters?: unknown } | undefined;
  const rawFilters = options && Array.isArray(options.filters) ? options.filters : [];
  const definitions: FilterDefinition[] = [];

  rawFilters.forEach((raw) => {
    const normalized = normalizeFilterDefinition(raw);
    if (normalized) {
      definitions.push(normalized);
    }
  });

  return definitions;
}

function normalizeFilterDefinition(raw: unknown): FilterDefinition | null {
  if (!raw || typeof raw !== "object") {
    return null;
  }

  const candidate = raw as Record<string, unknown>;
  const key = typeof candidate.key === "string" ? candidate.key.trim() : "";
  const typeValue = typeof candidate.type === "string" ? candidate.type.trim() : "";

  if (!key || !isFilterType(typeValue)) {
    return null;
  }

  const definition: FilterDefinition = {
    key,
    type: typeValue
  };

  if (typeof candidate.label === "string") {
    definition.label = candidate.label;
  }

  if (typeof candidate.placeholder === "string") {
    definition.placeholder = candidate.placeholder;
  }

  if (typeof candidate.multi === "boolean") {
    definition.multi = candidate.multi;
  }

  if (typeValue === "select") {
    const options = Array.isArray(candidate.options) ? candidate.options : [];
    definition.options = options
      .map((option) => {
        if (!option || typeof option !== "object") {
          return null;
        }
        const optionRecord = option as Record<string, unknown>;
        const value = typeof optionRecord.value === "string" ? optionRecord.value : null;
        if (!value) {
          return null;
        }
        const label = typeof optionRecord.label === "string" ? optionRecord.label : value;
        return { value, label };
      })
      .filter((entry): entry is { value: string; label: string } => Boolean(entry));
  }

  if (typeValue === "datetimeRange") {
    if (typeof candidate.fromLabel === "string") {
      definition.fromLabel = candidate.fromLabel;
    }
    if (typeof candidate.toLabel === "string") {
      definition.toLabel = candidate.toLabel;
    }
  }

  if (typeof candidate.queryParam === "string") {
    definition.queryParam = candidate.queryParam;
  } else if (candidate.queryParam && typeof candidate.queryParam === "object") {
    const queryParam = candidate.queryParam as Record<string, unknown>;
    const fromParam = typeof queryParam.from === "string" ? queryParam.from : undefined;
    const toParam = typeof queryParam.to === "string" ? queryParam.to : undefined;
    if (fromParam || toParam) {
      definition.queryParam = { from: fromParam, to: toParam };
    }
  }

  return definition;
}

function isFilterType(value: string): value is FilterType {
  return value === "text" || value === "multiText" || value === "select" || value === "datetimeRange";
}

function sanitizeFilterValues(
  values: unknown,
  definitions: FilterDefinition[]
): Record<string, unknown> | undefined {
  if (!values || typeof values !== "object" || definitions.length === 0) {
    return undefined;
  }

  const source = values as Record<string, unknown>;
  const result: Record<string, unknown> = {};

  definitions.forEach((definition) => {
    const sanitized = sanitizeFilterValue(source[definition.key], definition);
    if (sanitized !== undefined) {
      result[definition.key] = sanitized;
    }
  });

  return Object.keys(result).length > 0 ? result : undefined;
}

function sanitizeFilterValue(value: unknown, definition: FilterDefinition): unknown {
  switch (definition.type) {
    case "text": {
      if (typeof value !== "string") {
        return undefined;
      }
      const trimmed = value.trim();
      return trimmed.length > 0 ? trimmed : undefined;
    }
    case "multiText": {
      if (Array.isArray(value)) {
        const entries = ensureStringArray(value);
        return entries.length > 0 ? entries : undefined;
      }
      if (typeof value === "string") {
        const entries = parseMultiTextInput(value);
        return entries.length > 0 ? entries : undefined;
      }
      return undefined;
    }
    case "select": {
      if (definition.multi) {
        if (Array.isArray(value)) {
          const entries = ensureStringArray(value);
          return entries.length > 0 ? entries : undefined;
        }
        if (typeof value === "string") {
          const trimmed = value.trim();
          return trimmed.length > 0 ? [trimmed] : undefined;
        }
        return undefined;
      }

      if (Array.isArray(value)) {
        const [first] = ensureStringArray(value);
        return first ?? undefined;
      }
      if (typeof value === "string") {
        const trimmed = value.trim();
        return trimmed.length > 0 ? trimmed : undefined;
      }
      return undefined;
    }
    case "datetimeRange": {
      if (!value || typeof value !== "object") {
        return undefined;
      }
      const range = value as Record<string, unknown>;
      const from = coerceIsoDate(range.from);
      const to = coerceIsoDate(range.to);
      const result: DateRangeValue = {};
      if (from) {
        result.from = from;
      }
      if (to) {
        result.to = to;
      }
      return Object.keys(result).length > 0 ? result : undefined;
    }
    default:
      return undefined;
  }
}

function cloneFilterValues(
  values: Record<string, unknown> | undefined,
  definitions: FilterDefinition[]
): Record<string, unknown> {
  const sanitized = sanitizeFilterValues(values, definitions);
  if (!sanitized) {
    return {};
  }

  const clone: Record<string, unknown> = {};
  Object.entries(sanitized).forEach(([key, value]) => {
    clone[key] = cloneFilterValue(value);
  });
  return clone;
}

function cloneFilterValue(value: unknown): unknown {
  if (Array.isArray(value)) {
    return [...value];
  }
  if (isDateRangeValue(value)) {
    return { ...value };
  }
  return value;
}

function appendFilterQueryParams(
  params: URLSearchParams,
  filters: Record<string, unknown>,
  definitions: FilterDefinition[]
): void {
  definitions.forEach((definition) => {
    const value = filters[definition.key];
    if (value == null) {
      return;
    }

    const paramKey = getQueryParamKey(definition);
    if (definition.type === "datetimeRange") {
      if (!isDateRangeValue(value) || !paramKey || typeof paramKey === "string") {
        return;
      }
      if (paramKey.from && value.from) {
        params.set(paramKey.from, value.from);
      }
      if (paramKey.to && value.to) {
        params.set(paramKey.to, value.to);
      }
      return;
    }

    if (typeof paramKey !== "string") {
      return;
    }

    if (Array.isArray(value)) {
      params.delete(paramKey);
      value.forEach((entry) => {
        params.append(paramKey, String(entry));
      });
    } else if (typeof value === "string") {
      params.set(paramKey, value);
    }
  });
}

function removeFilterQueryParams(params: URLSearchParams, definitions: FilterDefinition[]): void {
  const keys = collectFilterQueryKeys(definitions);
  keys.forEach((key) => {
    if (key) {
      params.delete(key);
    }
  });
}

function collectFilterQueryKeys(definitions: FilterDefinition[]): string[] {
  const keys: string[] = [];
  definitions.forEach((definition) => {
    const key = getQueryParamKey(definition);
    if (!key) {
      return;
    }
    if (typeof key === "string") {
      keys.push(key);
    } else {
      if (key.from) {
        keys.push(key.from);
      }
      if (key.to) {
        keys.push(key.to);
      }
    }
  });
  return keys;
}

function getQueryParamKey(definition: FilterDefinition): string | { from?: string; to?: string } | null {
  if (definition.queryParam) {
    return definition.queryParam;
  }

  if (definition.type === "datetimeRange") {
    return {
      from: `filter[${definition.key}][from]`,
      to: `filter[${definition.key}][to]`
    };
  }

  const base = `filter[${definition.key}]`;
  if (definition.type === "multiText" || (definition.type === "select" && definition.multi)) {
    return `${base}[]`;
  }

  return base;
}

function ensureStringArray(values: unknown[]): string[] {
  return values
    .map((entry) => {
      if (typeof entry === "string") {
        return entry;
      }
      if (entry === null || entry === undefined) {
        return "";
      }
      return String(entry);
    })
    .map((entry) => entry.trim())
    .filter((entry) => entry.length > 0);
}

function parseMultiTextInput(value: string): string[] {
  if (typeof value !== "string" || value.trim().length === 0) {
    return [];
  }
  return value
    .split(/[\n,;]+/)
    .map((entry) => entry.trim())
    .filter((entry) => entry.length > 0);
}

function coerceIsoDate(value: unknown): string | undefined {
  if (typeof value !== "string") {
    return undefined;
  }
  const trimmed = value.trim();
  if (!trimmed) {
    return undefined;
  }
  const time = Date.parse(trimmed);
  if (Number.isNaN(time)) {
    return undefined;
  }
  return new Date(time).toISOString();
}

function convertLocalDateTimeToIso(value: string): string | undefined {
  if (!value) {
    return undefined;
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return undefined;
  }
  return date.toISOString();
}

function formatDateTimeLocal(value: string | undefined): string {
  if (!value) {
    return "";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

function isDateRangeValue(value: unknown): value is DateRangeValue {
  return Boolean(value) && typeof value === "object" && ("from" in (value as Record<string, unknown>) || "to" in (value as Record<string, unknown>));
}

